import moment from "moment";

// Alphanumeric: pad right with spaces, truncate if needed
const A = (v: string | number | null | undefined, len: number): string =>
  String(v ?? "").substring(0, len).padEnd(len, " ");

// Numeric: keep only digits, pad left with zeros
const N = (v: string | number | null | undefined, len: number): string =>
  String(v ?? "").replace(/\D/g, "").substring(0, len).padStart(len, "0");

// Money: integer * 100 → 15-digit string (13 int + 2 dec, no separator)
const money = (v: number | null | undefined): string =>
  Math.round(Math.abs(v ?? 0) * 100).toString().padStart(15, "0");

// Date to DDMMYYYY
const fmtDate = (v: string | null | undefined): string => {
  if (!v || v === "0001-01-01T00:00:00") return "00000000";
  return moment(v).format("DDMMYYYY");
};

// Strip non-digit chars
const digits = (v: string | null | undefined): string =>
  (v ?? "").replace(/\D/g, "");

const BANK_CODE = "748"; // Sicredi
const BANK_NAME = "SICREDI";
const LAYOUT_FILE_VERSION = "089";
const LAYOUT_LOTE_VERSION = "045";
const DENSITY = "01600";
const SERVICE_PIX = "45"; // PIX Transferência

export interface CnabCompany {
  cnpj: string;
  name: string;
  agency: string;
  agencyDv: string;
  account: string;
  accountDv: string;
  agencyAccountDv?: string;
  conventionCode?: string; // 20 chars, código do convênio no banco
}

export interface CnabProfessional {
  name: string;
  cpf?: string | null;
  cnpj?: string | null;
  email?: string | null;
  phone?: string | null; // ddd + mobilePhone, digits only
  pixKey?: string | null;
  pixKeyType?: "01" | "02" | "03" | "04" | "05" | null;
}

export interface CnabPayment {
  quoteId: number;
  professional: CnabProfessional;
  value: number;
  paymentDate: string | null;
}

// Derive PIX key type + value from professional data
function resolvePixKey(pro: CnabProfessional): { type: string; key: string } {
  if (pro.pixKey && pro.pixKeyType) {
    return { type: pro.pixKeyType, key: pro.pixKey };
  }
  const cpf = digits(pro.cpf);
  if (cpf.length === 11) return { type: "01", key: cpf };
  const cnpj = digits(pro.cnpj);
  if (cnpj.length === 14) return { type: "02", key: cnpj };
  if (pro.email) return { type: "04", key: pro.email };
  const phone = digits(pro.phone);
  if (phone.length >= 10) return { type: "03", key: `+55${phone}` };
  return { type: "01", key: "" }; // fallback: empty CPF
}

function headerArquivo(company: CnabCompany, fileSeq: number): string {
  let r = "";
  r += N(BANK_CODE, 3);                           // 001-003 Banco
  r += "0000";                                     // 004-007 Lote = 0000
  r += "0";                                        // 008 Tipo Registro
  r += A("", 9);                                   // 009-017 Uso FEBRABAN
  r += "2";                                        // 018 Tipo Inscrição (2=CNPJ)
  r += N(digits(company.cnpj), 14);               // 019-032 CNPJ
  r += A(company.conventionCode ?? "", 20);        // 033-052 Código Convênio
  r += N(company.agency, 5);                       // 053-057 Agência
  r += A(company.agencyDv, 1);                     // 058 DV Agência
  r += N(company.account, 12);                     // 059-070 Conta
  r += A(company.accountDv, 1);                    // 071 DV Conta
  r += A(company.agencyAccountDv ?? " ", 1);       // 072 DV Ag/Conta
  r += A(company.name.toUpperCase(), 30);          // 073-102 Nome Empresa
  r += A(BANK_NAME, 30);                           // 103-132 Nome Banco
  r += A("", 10);                                  // 133-142 Uso FEBRABAN
  r += "1";                                        // 143 Remessa (1=Remessa)
  r += moment().format("DDMMYYYY");               // 144-151 Data Geração
  r += moment().format("HHmmss");                 // 152-157 Hora Geração
  r += N(fileSeq, 6);                              // 158-163 Nº Seq Arquivo
  r += N(LAYOUT_FILE_VERSION, 3);                  // 164-166 Versão Layout
  r += N(DENSITY, 5);                              // 167-171 Densidade
  r += A("", 20);                                  // 172-191 Reservado Banco
  r += A("", 20);                                  // 192-211 Reservado Empresa
  r += A("", 29);                                  // 212-240 Uso FEBRABAN
  return r;
}

function headerLote(company: CnabCompany, lote: number): string {
  let r = "";
  r += N(BANK_CODE, 3);                           // 001-003 Banco
  r += N(lote, 4);                                 // 004-007 Lote
  r += "1";                                        // 008 Tipo Registro
  r += "C";                                        // 009 Tipo Operação (C=Crédito)
  r += N(SERVICE_PIX, 2);                          // 010-011 Tipo Serviço (45=PIX)
  r += "00";                                       // 012-013 Uso FEBRABAN
  r += N(LAYOUT_LOTE_VERSION, 3);                  // 014-016 Versão Layout Lote
  r += " ";                                        // 017 Uso FEBRABAN
  r += "2";                                        // 018 Tipo Inscrição (2=CNPJ)
  r += N(digits(company.cnpj), 14);               // 019-032 CNPJ
  r += A(company.conventionCode ?? "", 20);        // 033-052 Código Convênio
  r += N(company.agency, 5);                       // 053-057 Agência
  r += A(company.agencyDv, 1);                     // 058 DV Agência
  r += N(company.account, 12);                     // 059-070 Conta
  r += A(company.accountDv, 1);                    // 071 DV Conta
  r += A(company.agencyAccountDv ?? " ", 1);       // 072 DV Ag/Conta
  r += A(company.name.toUpperCase(), 30);          // 073-102 Nome Empresa
  r += A("", 30);                                  // 103-132 Mensagem
  r += N(1, 10);                                   // 133-142 Nº Remessa
  r += moment().format("DDMMYYYY");               // 143-150 Data Gravação
  r += "00000000";                                 // 151-158 Data Crédito
  r += A("", 72);                                  // 159-230 Uso FEBRABAN
  r += A("", 10);                                  // 231-240 Uso FEBRABAN
  return r;
}

function segmentoA(lote: number, seq: number, pmt: CnabPayment): string {
  let r = "";
  r += N(BANK_CODE, 3);                                          // 001-003 Banco
  r += N(lote, 4);                                               // 004-007 Lote
  r += "3";                                                      // 008 Tipo Registro (Detalhe)
  r += N(seq, 5);                                                // 009-013 Nº Seq
  r += "A";                                                      // 014 Segmento
  r += "0";                                                      // 015 Tipo Movimento (0=Inclusão)
  r += "00";                                                     // 016-017 Código Instrução
  r += "009";                                                    // 018-020 Câmara (009=PIX)
  r += N(0, 3);                                                  // 021-023 Banco Favorecido (zeros=PIX)
  r += N(0, 5);                                                  // 024-028 Agência Favorecido
  r += " ";                                                      // 029 DV Agência
  r += N(0, 12);                                                 // 030-041 Conta Favorecido
  r += " ";                                                      // 042 DV Conta
  r += " ";                                                      // 043 DV Ag/Conta
  r += A(pmt.professional.name.toUpperCase(), 30);               // 044-073 Nome Favorecido
  r += A(String(pmt.quoteId), 20);                               // 074-093 Nº Documento Empresa
  r += fmtDate(pmt.paymentDate);                                 // 094-101 Data Pagamento
  r += "BRL";                                                    // 102-104 Tipo Moeda
  r += N(0, 15);                                                 // 105-119 Qtd Moeda
  r += money(pmt.value);                                         // 120-134 Valor Pagamento
  r += A("", 20);                                                // 135-154 Nº Documento Banco
  r += "00000000";                                               // 155-162 Data Real Pagamento
  r += N(0, 15);                                                 // 163-177 Valor Real Pagamento
  r += A(`ORC ${pmt.quoteId}`, 40);                              // 178-217 Informação 2
  r += "00";                                                     // 218-219 Cód Finalidade TED
  r += "  ";                                                     // 220-221 Compl Finalidade
  r += A("", 3);                                                 // 222-224 Uso FEBRABAN
  r += "0";                                                      // 225 Aviso Favorecido
  r += A("", 15);                                                // 226-240 Ocorrências
  return r;
}

function segmentoB(lote: number, seq: number, pmt: CnabPayment): string {
  const pro = pmt.professional;
  const { type: pixKeyType, key: pixKey } = resolvePixKey(pro);

  // Beneficiary identification (CPF or CNPJ)
  const cpfDigits = digits(pro.cpf);
  const cnpjDigits = digits(pro.cnpj);
  const hasCnpj = cnpjDigits.length === 14;
  const tipoInscricao = hasCnpj ? "02" : "01";
  const nrInscricao = hasCnpj ? cnpjDigits : cpfDigits;

  let r = "";
  r += N(BANK_CODE, 3);           // 001-003 Banco
  r += N(lote, 4);                // 004-007 Lote
  r += "3";                       // 008 Tipo Registro (Detalhe)
  r += N(seq, 5);                 // 009-013 Nº Seq
  r += "B";                       // 014 Segmento
  r += "0";                       // 015 Tipo Movimento
  r += "00";                      // 016-017 Código Instrução
  r += N(tipoInscricao, 2);       // 018-019 Tipo Inscrição Favorecido
  r += N(nrInscricao, 14);        // 020-033 Nº Inscrição Favorecido
  r += A("", 30);                 // 034-063 Logradouro
  r += N(0, 5);                   // 064-068 Número Local
  r += A("", 15);                 // 069-083 Complemento
  r += A("", 15);                 // 084-098 Cidade
  r += N(0, 5);                   // 099-103 CEP
  r += N(0, 3);                   // 104-106 Complemento CEP
  r += A("", 2);                  // 107-108 Estado
  r += A("", 2);                  // 109-110 Uso FEBRABAN
  r += "00000000";                // 111-118 Data Vencimento
  r += N(0, 15);                  // 119-133 Valor Desconto
  r += N(0, 15);                  // 134-148 Valor Abatimento
  r += N(0, 15);                  // 149-163 Valor Mora/Juros
  r += A("", 15);                 // 164-178 Cód Doc Favorecido
  r += A("", 8);                  // 179-186 Uso FEBRABAN
  r += pixKeyType.padStart(2, "0"); // 187-188 Tipo Chave PIX
  r += A(pixKey, 52);             // 189-240 Chave PIX
  return r;
}

function trailerLote(
  lote: number,
  qtdRegistros: number,
  totalValue: number
): string {
  // Somatória: 18 digits (16 int + 2 dec)
  const totalStr = Math.round(totalValue * 100).toString().padStart(18, "0");
  let r = "";
  r += N(BANK_CODE, 3);    // 001-003
  r += N(lote, 4);          // 004-007
  r += "5";                 // 008 Tipo Registro (Trailer Lote)
  r += A("", 9);            // 009-017 Uso FEBRABAN
  r += N(qtdRegistros, 6); // 018-023 Qtd Registros no Lote
  r += totalStr;            // 024-041 Somatória Valores (18)
  r += N(0, 18);            // 042-059 Somatória Qtd Moeda
  r += N(0, 6);             // 060-065 Nº Aviso Débito
  r += A("", 165);          // 066-230 Uso FEBRABAN
  r += A("", 10);           // 231-240 Ocorrências
  return r;
}

function trailerArquivo(qtdLotes: number, qtdRegistros: number): string {
  let r = "";
  r += N(BANK_CODE, 3);   // 001-003
  r += "9999";              // 004-007 Lote = 9999
  r += "9";                 // 008 Tipo Registro (Trailer Arquivo)
  r += A("", 9);            // 009-017 Uso FEBRABAN
  r += N(qtdLotes, 6);      // 018-023 Qtd Lotes
  r += N(qtdRegistros, 6); // 024-029 Qtd Registros
  r += N(0, 6);             // 030-035 Qtd Contas Conciliação
  r += A("", 205);          // 036-240 Uso FEBRABAN
  return r;
}

export function generateCnab240(
  payments: CnabPayment[],
  company: CnabCompany
): string {
  const lines: string[] = [];

  lines.push(headerArquivo(company, 1));

  const lote = 1;
  lines.push(headerLote(company, lote));

  let seqInLote = 1;
  let totalValue = 0;

  for (const pmt of payments) {
    lines.push(segmentoA(lote, seqInLote++, pmt));
    lines.push(segmentoB(lote, seqInLote++, pmt));
    totalValue += pmt.value ?? 0;
  }

  // qtdRegistros no lote = headerLote(1) + segmentos(n*2) + trailerLote(1)
  const qtdRegistrosLote = 2 + payments.length * 2;
  lines.push(trailerLote(lote, qtdRegistrosLote, totalValue));

  // qtdRegistros total = headerArquivo(1) + linhasLote + trailerArquivo(1)
  const qtdTotal = lines.length + 1;
  lines.push(trailerArquivo(1, qtdTotal));

  // Validate all lines are exactly 240 chars
  lines.forEach((line, i) => {
    if (line.length !== 240) {
      console.error(`[CNAB240] Linha ${i + 1}: ${line.length} chars (esperado 240)`);
    }
  });

  return lines.join("\r\n");
}
